import { useEffect, useState } from "react";
import api from "../api";

/**
 * Deactivate reviewed sites (ROADMAP B3).
 *
 * Supervisors and programme coordinators work the site review list, tick what
 * looks finished, and either send it out for field verification or retire it.
 * Field techs are not in this loop by design -- they work in FieldSeeker and
 * do not touch site records, so nothing here asks anything of them.
 *
 * The trigger is a bar pinned to the bottom of the viewport and the form is a
 * modal, rather than a panel below the table. Deciding whether a site should
 * be retired means reading its history in the table, so an action that forces
 * you to scroll away from the evidence to reach it -- and back up again to
 * check the next one -- gets in the way of the actual judgement.
 *
 * Preview always runs before a write, and the write is a separate, explicit
 * press. The backend defaults to previewing too, so a request that loses the
 * confirm flag shows what would change rather than changing it.
 *
 * Who and when are not collected: editor tracking on PointLocation stamps the
 * signed-in supervisor's own ArcGIS account on every edit, which is better
 * evidence than a name this form could ask for.
 */

export interface SelectableSite {
  pointlocid: string;
  name: string;
  subzone: string;
  location_name?: string;
  last_inspection?: string;
}

interface Planned {
  global_id: string;
  name: string;
  subzone: string;
  note_written: boolean;
  comments_after: string;
}

interface Outcome {
  planned: Planned[];
  skipped: { global_id: string; name?: string; why: string }[];
  note: string;
  dry_run: boolean;
  written: number;
  failures: { global_id: string; message: string }[];
}

const REASONS: [string, string][] = [
  ["consecutive_dry", "Dry all season"],
  ["no_treatment", "No treatment needed"],
  ["site_removed", "Site gone"],
  ["duplicate", "Duplicate site"],
  ["other", "Other"],
];

function csv(rows: (string | number)[][]) {
  return rows
    .map((r) => r.map((v) => `"${String(v ?? "").replace(/"/g, '""')}"`).join(","))
    .join("\n");
}

function download(name: string, body: string) {
  const url = URL.createObjectURL(new Blob([body], { type: "text/csv;charset=utf-8" }));
  const a = document.createElement("a");
  a.href = url;
  a.download = name;
  a.click();
  URL.revokeObjectURL(url);
}

export default function SiteDeactivationPanel({
  sites,
  onClear,
  onDeactivated,
}: {
  sites: SelectableSite[];
  onClear: () => void;
  onDeactivated: (ids: string[]) => void;
}) {
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState("consecutive_dry");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState<"" | "preview" | "write">("");
  const [outcome, setOutcome] = useState<Outcome | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Escape closes, unless a write is in flight -- losing the result of an
  // edit that already reached AGOL would leave you unsure what happened.
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape" && busy !== "write") setOpen(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, busy]);

  const reset = () => {
    setOutcome(null);
    setError(null);
  };

  const run = async (confirm: boolean) => {
    setBusy(confirm ? "write" : "preview");
    setError(null);
    try {
      const r = await api.post("/deactivate-sites", {
        global_ids: sites.map((s) => s.pointlocid),
        reason,
        note,
        confirm,
      });
      setOutcome(r.data);
      if (confirm && r.data?.written > 0) {
        onDeactivated(r.data.planned.map((p: Planned) => p.global_id));
      }
    } catch (e: any) {
      setError(e?.response?.data?.message || "The request failed.");
    } finally {
      setBusy("");
    }
  };

  /** The field-crew round trip. No write -- this list goes out, and whatever
   *  comes back is reviewed here next time. */
  const exportForVerification = () =>
    download(
      `Site_Verification_${new Date().toISOString().slice(0, 10)}.csv`,
      csv([
        ["Site Name", "Subzone", "Location", "Last Inspection", "Verified? (Y/N)", "Notes"],
        ...sites.map((s) => [
          s.name || "",
          s.subzone || "",
          s.location_name || "",
          s.last_inspection || "",
          "",
          "",
        ]),
      ])
    );

  const wrote = outcome && !outcome.dry_run;
  const noNote = outcome?.planned.filter((p) => !p.note_written).length ?? 0;
  const n = sites.length;

  return (
    <>
      {/* Pinned so the action is always one click away from the evidence. */}
      <div className="fixed bottom-0 inset-x-0 z-40 border-t border-line bg-card shadow-lg2">
        <div className="max-w-content mx-auto px-4 py-2.5 flex flex-wrap items-center gap-2">
          <span className="font-bold text-[14px]">
            {n} site{n === 1 ? "" : "s"} selected
          </span>
          <button onClick={onClear} className="text-xs text-ink-faint hover:underline">
            Clear
          </button>
          <div className="ml-auto flex items-center gap-2">
            <button onClick={exportForVerification} className="btn-ghost text-sm !py-1.5">
              📋 Send for verification
            </button>
            <button
              onClick={() => {
                reset();
                setOpen(true);
              }}
              className="btn-primary text-sm !py-1.5"
            >
              Deactivate…
            </button>
          </div>
        </div>
      </div>
      {/* Keeps the pinned bar from covering the last table rows. */}
      <div className="h-16" aria-hidden />

      {open && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4"
          role="dialog"
          aria-modal="true"
          aria-label="Deactivate sites"
        >
          <div
            className="absolute inset-0 bg-ink/40"
            onClick={() => busy !== "write" && setOpen(false)}
          />
          <div className="relative bg-card rounded-card shadow-lg2 w-full max-w-lg max-h-[85vh] overflow-auto p-5">
            <div className="flex items-baseline gap-2">
              <h3 className="font-extrabold text-lg">
                Deactivate {n} site{n === 1 ? "" : "s"}
              </h3>
              <button
                onClick={() => setOpen(false)}
                disabled={busy === "write"}
                className="ml-auto text-ink-faint hover:text-ink text-xl leading-none disabled:opacity-40"
                aria-label="Close"
              >
                ×
              </button>
            </div>

            <p className="text-ink-soft text-[13px] mt-1">
              Sets each site inactive and changes its map symbol to the grey circle,
              matching what FieldSeeker does. Your ArcGIS account is recorded on the
              edit automatically.
            </p>

            <div className="eyebrow mt-4 mb-1.5">Reason</div>
            <div className="flex flex-wrap gap-2">
              {REASONS.map(([k, label]) => (
                <button
                  key={k}
                  onClick={() => {
                    setReason(k);
                    reset();
                  }}
                  className={`pill ${
                    reason === k
                      ? "bg-brand-tint text-brand-deep"
                      : "bg-paper-2 text-ink-faint"
                  }`}
                >
                  {label}
                </button>
              ))}
            </div>

            <input
              value={note}
              onChange={(e) => {
                setNote(e.target.value);
                reset();
              }}
              maxLength={60}
              placeholder={
                reason === "other" ? "Say why (required for Other)" : "Optional extra detail"
              }
              className="w-full mt-3 px-3 py-2 border border-line-strong rounded-sm2 text-[13px]"
            />

            {error && (
              <div className="mt-3 p-3 rounded-sm2 bg-wnv-tint text-wnv text-[13px]">
                {error}
              </div>
            )}

            {outcome && (
              <div className="mt-3 text-[13px]">
                {wrote ? (
                  <div className="p-3 rounded-sm2 bg-brand-tint text-brand-deep font-semibold">
                    Deactivated {outcome.written} site{outcome.written === 1 ? "" : "s"}.
                    {outcome.failures.length > 0 &&
                      ` ${outcome.failures.length} failed — the batch rolls back together, so nothing was written.`}
                  </div>
                ) : (
                  <div className="p-3 rounded-sm2 bg-info-tint text-info">
                    <strong>{outcome.planned.length}</strong> would be deactivated, with the
                    note <code className="font-mono text-[12px]">{outcome.note}</code>.
                    {noNote > 0 && (
                      <>
                        {" "}
                        {noNote} already {noNote === 1 ? "has a comment" : "have comments"} too
                        long to append to — {noNote === 1 ? "it" : "they"} will still be
                        deactivated, just without the note. Nothing existing is overwritten.
                      </>
                    )}
                  </div>
                )}

                {outcome.skipped.length > 0 && (
                  <div className="mt-2 p-3 rounded-sm2 bg-paper-2 text-ink-soft">
                    Skipped {outcome.skipped.length}:{" "}
                    {outcome.skipped
                      .slice(0, 4)
                      .map((s) => `${s.name || s.global_id.slice(0, 8)} (${s.why})`)
                      .join(", ")}
                    {outcome.skipped.length > 4 && ` and ${outcome.skipped.length - 4} more`}
                  </div>
                )}

                {outcome.failures.length > 0 && (
                  <div className="mt-2 p-3 rounded-sm2 bg-wnv-tint text-wnv">
                    {outcome.failures.slice(0, 3).map((f) => (
                      <div key={f.global_id}>
                        {f.global_id.slice(0, 8)}: {f.message}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            <div className="flex flex-wrap items-center gap-2 mt-4 pt-3 border-t border-line">
              {wrote ? (
                <button onClick={() => setOpen(false)} className="btn-primary text-sm">
                  Done
                </button>
              ) : (
                <>
                  <button
                    onClick={() => run(false)}
                    disabled={!!busy || (reason === "other" && !note.trim())}
                    className="btn-ghost text-sm"
                  >
                    {busy === "preview" ? "Checking…" : "Preview changes"}
                  </button>
                  <button
                    onClick={() => run(true)}
                    disabled={!!busy || !outcome || outcome.planned.length === 0}
                    className="btn-primary text-sm"
                  >
                    {busy === "write"
                      ? "Writing…"
                      : `Confirm${outcome ? ` (${outcome.planned.length})` : ""}`}
                  </button>
                  {!outcome && (
                    <span className="text-[12px] text-ink-faint">
                      Preview first — nothing is written until you confirm.
                    </span>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
}
